import {
  Alert,
  Attributes,
  Button,
  Card,
  openConfirmDialog,
  Separator,
  showNotification,
} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {
  useCheckWhatsAppConnectionMutation,
  useDisconnectWhatsAppMutation,
  WhatsAppConnectionProblemReason,
  WhatsAppConnectionResponseBody,
  WhatsAppConnectionVerificationResponseBody,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {parseDate} from 'shared/web';

import {WhatsAppVerification} from '../WhatsAppVerification/WhatsAppVerification';

interface WhatsAppConnectionStatusProps {
  connection: WhatsAppConnectionResponseBody;
  onReconnect: () => void;
}

const getVerificationLabel = (verification: WhatsAppConnectionVerificationResponseBody) =>
  verification.state === 'verified'
    ? i18n.t('page.settings.telephony.verificationPassed')
    : i18n.t('page.settings.whatsApp.connection.notVerified');

const getProblemMessage = (reason: WhatsAppConnectionProblemReason) =>
  match(reason)
    .with('invalid_token', () => i18n.t('page.settings.whatsApp.connection.problem.invalidToken'))
    .with('permission_revoked', () =>
      i18n.t('page.settings.whatsApp.connection.problem.permissionRevoked')
    )
    .with('webhook_not_routed', () =>
      i18n.t('page.settings.whatsApp.connection.problem.webhookNotRouted')
    )
    .exhaustive();

export function WhatsAppConnectionStatus(props: WhatsAppConnectionStatusProps) {
  const formatDateTime = useDateTimeFormatter();
  const [checkConnection, {isLoading: isChecking}] = useCheckWhatsAppConnectionMutation();
  const [disconnect, {isLoading: isDisconnecting}] = useDisconnectWhatsAppMutation();

  const configuredAt = props.connection.configuredAt;
  const problem = props.connection.problem;
  // The backend rejects verification and checks while the connection is disabled.
  const isEnabled = props.connection.configurationState === 'configured';

  const handleCheck = () =>
    checkConnection()
      .unwrap()
      .then((checked) => {
        if (isNil(checked.problem)) {
          showNotification.success(i18n.t('page.settings.whatsApp.connection.checkPassed'));
        }
      })
      .catch(handleApiError);

  const handleDisconnect = () =>
    openConfirmDialog({
      text: i18n.t('page.settings.whatsApp.connection.disconnectConfirm'),
      onConfirm: () =>
        disconnect()
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('page.settings.whatsApp.connection.disconnected'))
          )
          .catch(handleApiError),
    });

  return (
    <Card title={i18n.t('page.settings.whatsApp.connection.connectedTitle')} variant="inlineGrey">
      <VStack spacing={4}>
        <Show when={problem}>
          {(problem) => (
            <Alert
              layout="detailed"
              variant="error"
              title={getProblemMessage(problem.reason)}
              message={
                isNil(problem.metaErrorCode)
                  ? undefined
                  : i18n.t('page.settings.whatsApp.connection.metaErrorCode', {
                      code: problem.metaErrorCode,
                    })
              }
              actions={[
                {
                  title: i18n.t('page.settings.whatsApp.connection.reconnect'),
                  variant: 'primary',
                  onClick: props.onReconnect,
                },
              ]}
            />
          )}
        </Show>
        <Attributes
          rows={[
            {
              label: i18n.t('general.labels.status'),
              value:
                props.connection.configurationState === 'disabled'
                  ? i18n.t('page.settings.facebookCredentials.disabled')
                  : i18n.t('page.myProfile.receivingMailbox.status.connected'),
            },
            {
              label: i18n.t('page.settings.whatsApp.phoneNumber'),
              value: props.connection.phoneNumberId,
            },
            {
              label: i18n.t('page.settings.whatsApp.wabaId'),
              value: props.connection.wabaId,
            },
            {
              label: i18n.t('page.settings.whatsApp.connection.configuredAt'),
              value: isNil(configuredAt)
                ? null
                : formatDateTime('dateTimeShort', parseDate(configuredAt)),
            },
            {
              label: i18n.t('page.settings.whatsApp.connection.receiving'),
              value: getVerificationLabel(props.connection.receivingVerification),
            },
            {
              label: i18n.t('page.settings.whatsApp.connection.sending'),
              value: getVerificationLabel(props.connection.sendingVerification),
            },
          ]}
        />
        <Show when={isEnabled}>
          <WhatsAppVerification />
        </Show>
        <Separator />
        <HStack spacing={2}>
          <Show when={isEnabled}>
            <Button
              variant="secondary"
              title={i18n.t('page.settings.whatsApp.connection.check')}
              isLoading={isChecking}
              onClick={handleCheck}
            />
          </Show>
          <Button
            variant="secondary"
            title={i18n.t('page.settings.whatsApp.connection.reconnect')}
            onClick={props.onReconnect}
          />
          <Button
            variant="dangerOutlined"
            title={i18n.t('page.settings.whatsApp.connection.disconnect')}
            isLoading={isDisconnecting}
            onClick={handleDisconnect}
          />
        </HStack>
      </VStack>
    </Card>
  );
}
